import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext } from "react";
import { useAuth } from "../context/authContext";
import { BikeContext } from "../context/bikeContext";
import {
  Bike,
  deleteBike,
  getBike,
  listBikes,
  Paginated,
  postBike,
  PutBike,
  putBike,
} from "../http/onlyclicksApi";

export const useBikes = (pageSize?: number, enabled: boolean = true) => {
  const auth = useAuth();
  return useQuery({
    queryKey: ["bikes", pageSize],
    queryFn: (ctx) => listBikes(ctx.signal, pageSize),
    enabled: auth.hasActiveSession && enabled,
  });
};

export const useInfiniteBikes = () => {
  const auth = useAuth();
  return useInfiniteQuery({
    queryKey: ["inf-bikes"],
    queryFn: (ctx) => listBikes(ctx.signal, ctx.pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_page_token,
    getPreviousPageParam: (firstPage) => firstPage.next_page_token,
    enabled: auth.hasActiveSession,
    // placeholderData: keepPreviousData, // Preserve data between queryKey changes
    select: (data) => data?.pages.flatMap((p) => p.items),
  });
};

export const useBike = (bikeId?: string) => {
  const auth = useAuth();
  return useQuery({
    queryKey: ["bike", bikeId],
    queryFn: (ctx) => getBike(ctx.signal, bikeId!),
    enabled: auth.hasActiveSession && !!bikeId,
  });
};

export const useBikeMutation = () => {
  const { setBike } = useContext(BikeContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newBike: Omit<Bike, "id" | "created_at">) => postBike(newBike),
    onSuccess: (bike) => {
      queryClient.setQueryData(["bike", bike.id], bike);

      const cached = queryClient.getQueryData(["bikes"]) as
        | Paginated<Bike>
        | undefined;
      if (cached?.items?.length === 0) {
        const updated = {
          ...cached,
          items: [bike],
        };
        queryClient.setQueryData(["bikes"], updated);
      } else {
        queryClient.invalidateQueries({ queryKey: ["bikes"] });
      }

      return setBike(bike);
    },
  });
};

export const useUpdateBike = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bike }: { id: string; bike: PutBike }) => {
      return putBike(id, bike);
    },
    onSuccess: (bike) => {
      queryClient.setQueryData(["bike", bike.id], bike);
    },
  });
};

export const useDeleteBike = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBike(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bikes"] });
    },
  });
};
